import { Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import QRCode from 'react-qr-code';

import { ADDRESS_BOOK_BFF_QUERY_KEYS } from '@/api/address-book-bff/queries';
import { _postContactCode } from '@/api/user/mutations';
import { queryClient } from '@/app/providers/react-query-provider';
import { Modal, Spinner, theme, Tooltip, Typography } from '@/design-system';
import { usePersonalQr } from '@/features/qr/use-personal-qr';
import { useCopy } from '@/global-hooks/use-copy';
import { useGlobalModal } from '@/global-hooks/use-global-modal';

import { CopyCodeButton, CopyCodeCell } from './qr-code-modal.styles';

const CONTACTS_POLL_INTERVAL_MS = 5000;

const formatCountdown = (seconds: number) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
};

export const QrCodeModal = () => {
  const { handleClose, open } = useGlobalModal('QR_CODE_MODAL');

  const [secondsLeft, setSecondsLeft] = useState<number | null>(null);

  const { qrValue, isFallback } = usePersonalQr(open);

  const {
    data: contactCodeData,
    isFetching: isContactCodeFetching,
    refetch,
  } = useQuery({
    queryKey: ['contact-code-generate'],
    queryFn: _postContactCode,
    enabled: open,
    staleTime: 0,
    gcTime: 0,
    refetchOnWindowFocus: false,
  });

  // Gated on qrValue alone: the six-digit code is a secondary affordance with its own
  // retry/backoff and can arrive later without blocking the QR code the member is
  // waiting on to check in or connect.
  const isLoading = !qrValue;
  const { isTooltipOpen, handleClick: handleCopyCode } = useCopy({
    textToCopy: contactCodeData?.code,
  });

  useEffect(() => {
    if (!open || !contactCodeData?.expiresInSeconds) return;

    const expiresAt = Date.now() + contactCodeData.expiresInSeconds * 1000;
    const getRemaining = () => Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));

    setSecondsLeft(getRemaining());

    const countdownInterval = setInterval(() => setSecondsLeft(getRemaining()), 1000);
    const refetchTimeout = setTimeout(refetch, contactCodeData.expiresInSeconds * 1000);

    return () => {
      clearInterval(countdownInterval);
      clearTimeout(refetchTimeout);
    };
  }, [contactCodeData?.code, contactCodeData?.expiresInSeconds, open, refetch]);

  useEffect(() => {
    if (!open) return;

    const pollInterval = setInterval(() => {
      queryClient.invalidateQueries({
        queryKey: [ADDRESS_BOOK_BFF_QUERY_KEYS.contacts],
        refetchType: 'all',
      });
    }, CONTACTS_POLL_INTERVAL_MS);

    return () => clearInterval(pollInterval);
  }, [open]);

  return (
    <Modal
      open={open}
      onOpenChange={() => handleClose()}
      title="Your Personal QR Code"
      variant="center"
      maxWidth={504}
    >
      <Stack flexDirection="column" alignItems="center" gap="16px">
        <Stack flexDirection="column" gap="8px">
          <Typography variant="body2" fontWeight={500} color={theme.colors.text.secondary}>
            Your personal QR code for Gamma events:
          </Typography>
          <Typography
            as="ul"
            variant="body2"
            color={theme.colors.text.secondary}
            style={{ margin: 0, paddingLeft: theme.spacing[16] }}
          >
            <li>Show to event staff for check-in</li>
            <li>Share with other attendees to connect and message on Gamma</li>
          </Typography>
        </Stack>

        {isLoading ? (
          <Spinner />
        ) : (
          qrValue && (
            <QRCode
              value={qrValue}
              size={210}
              bgColor={theme.colors.background.canvas}
              fgColor={theme.colors.white[100]}
            />
          )
        )}

        {isFallback && (
          <Typography
            variant="body3"
            fontWeight={500}
            color={theme.colors.text.secondary}
            align="center"
          >
            {/* True whether the mint failed because of no connection, a server error, or the
                rate limit — none of those are distinguishable here, and a mint that reached the
                server also fires the global error toast, so this can't claim a specific cause
                without contradicting it. */}
            This code still works for event check-in. Connecting will resume shortly.
          </Typography>
        )}

        {!isContactCodeFetching && contactCodeData && (
          <Stack flexDirection="column" alignItems="center" gap="8px">
            <Typography variant="body2" fontWeight={500} color={theme.colors.text.secondary}>
              Or you can share this code:
            </Typography>

            <Tooltip title="Copied" open={isTooltipOpen}>
              <CopyCodeButton type="button" aria-label="Copy share code" onClick={handleCopyCode}>
                {contactCodeData.code.split('').map((digit, index) => (
                  <CopyCodeCell key={`${index}-${digit}`} aria-hidden="true">
                    {digit}
                  </CopyCodeCell>
                ))}
              </CopyCodeButton>
            </Tooltip>

            {secondsLeft !== null && (
              <Typography variant="body3" fontWeight={500} color={theme.colors.text.secondary}>
                Code will be regenerated in {formatCountdown(secondsLeft)} min
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </Modal>
  );
};
